import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { hasSessionData, sumDayVolume } from './workout';
import { buildWeightSeries } from './body';
import { todayKey } from './state';
import { buildRecords } from './logic/records';
import { suggestAliases } from './logic/muscles';
import { complianceSoFar, sessionStreak, weeklyStreak } from './logic/schedule';
import { Card, StatTile } from './ui';
import { PrWallCard } from './stats/PrWallCard';
import { ProgressCard } from './stats/ProgressCard';
import { LiftStatusCard } from './stats/LiftStatusCard';
import { MuscleVolumeCard } from './stats/MuscleVolumeCard';
import { WeekReviewCard } from './stats/WeekReviewCard';
import { BodyCard } from './stats/BodyCard';
import { ExerciseNamesSheet } from './stats/ExerciseNamesSheet';
import { liftStatusBoard, muscleVolume } from './stats/statsData';
import { NutritionView } from './stats/NutritionView';
import { estimateEnergyBalance } from './logic/energy';
import { weightGoalProgress } from './logic/goal';
import { trainingLogs } from './logic/deload';
import { GoalProgressCard } from './body/WeightGoal';
import { Segmented } from './stats/common';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Stats tab                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

// Per device: which view Stats opens on (not synced, so try/catch only).
const VIEW_KEY = 'xceller_fit_stats_view';
const VIEWS = [
  { id: 'training', label: 'Training' },
  { id: 'nutrition', label: 'Nutrition' },
];

function readView() {
  try {
    return localStorage.getItem(VIEW_KEY) === 'nutrition' ? 'nutrition' : 'training';
  } catch {
    return 'training';
  }
}

export function StatsTab({ t, state, setState, isDark = true, onOpenMeals }) {
  const [view, setViewState] = useState(readView);
  const setView = useCallback((v) => {
    setViewState(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch {}
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Segmented t={t} label="Stats view" options={VIEWS} value={view} onChange={setView} />
      {view === 'nutrition' ? (
        <NutritionView t={t} isDark={isDark} state={state} setState={setState} today={todayKey()} onOpenDay={onOpenMeals} />
      ) : (
        <TrainingView t={t} state={state} setState={setState} isDark={isDark} />
      )}
    </div>
  );
}

function TrainingView({ t, state, setState, isDark }) {
  const {
    logs, bodyMetrics, plan, programs, exerciseMeta, exerciseAliases, profile, deloads,
  } = state;
  const goal = profile?.goal;
  const customLandmarks = profile?.volumeLandmarks;
  const today = todayKey();
  const [progressExercise, setProgressExercise] = useState('');
  const [namesOpen, setNamesOpen] = useState(false);
  const progressRef = useRef(null);

  const weightSeries = useMemo(() => buildWeightSeries(bodyMetrics), [bodyMetrics]);
  // The weight goal, with maintenance from the energy estimate for a kcal suggestion.
  const { nutrition, logStatus, supplements } = state;
  const weightGoal = useMemo(() => {
    if (!profile?.weightGoal) return null;
    const nState = { nutrition, logStatus, profile, bodyMetrics, supplements };
    const est = estimateEnergyBalance(nState, today);
    return weightGoalProgress(nState, today, { tdee: est && est.ok ? est.tdee : undefined });
  }, [nutrition, logStatus, profile, bodyMetrics, supplements, today]);
  const goalWeight = weightGoal ? weightGoal.target : null;

  const allLoggedExercises = useMemo(() => {
    const names = new Set();
    Object.values(logs || {}).forEach(dayLog => {
      Object.entries(dayLog || {}).forEach(([name, sets]) => {
        if ((sets || []).some(s => s?.done && (Number(s.weight) || 0) > 0)) names.add(name);
      });
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [logs]);

  const stats = useMemo(() => {
    const days = Object.entries(logs).filter(([, log]) => hasSessionData(log));
    const total = days.length;
    const cutoff30 = new Date(); cutoff30.setDate(cutoff30.getDate() - 30);
    const last30 = days.filter(([k]) => new Date(`${k}T12:00:00`) >= cutoff30);
    const last30Volume = last30.reduce((s, [, log]) => s + sumDayVolume(log), 0);

    // Volume per week (last 8 weeks)
    const weeks = [];
    const todayD = new Date();
    for (let i = 7; i >= 0; i -= 1) {
      const wEnd = new Date(todayD); wEnd.setDate(todayD.getDate() - i * 7);
      const wStart = new Date(wEnd); wStart.setDate(wEnd.getDate() - 6);
      let vol = 0; let sessions = 0;
      days.forEach(([k, log]) => {
        const d = new Date(`${k}T12:00:00`);
        if (d >= wStart && d <= wEnd) { vol += sumDayVolume(log); sessions += 1; }
      });
      weeks.push({ label: wStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }), volume: Math.round(vol), sessions });
    }

    return { total, last30Count: last30.length, last30Volume, weeks };
  }, [logs]);

  // The heavy derivations rerun only when their inputs change, not on every
  // render of the tab (Mo has 100+ days of logs).
  const records = useMemo(() => buildRecords(logs, { exerciseMeta, today }), [logs, exerciseMeta, today]);
  const liftRows = useMemo(
    // Deload sessions would read as a drop: the board leaves them out.
    () => liftStatusBoard({ logs: trainingLogs({ logs, deloads }), plan, programs, exerciseMeta, profile: { goal } }, today),
    [logs, deloads, plan, programs, exerciseMeta, goal, today]
  );
  const volume = useMemo(
    () => muscleVolume({ logs, plan, exerciseMeta, exerciseAliases, profile: { volumeLandmarks: customLandmarks } }, today),
    [logs, plan, exerciseMeta, exerciseAliases, customLandmarks, today]
  );

  // Default chart: the lift of the latest PR, else the most flagged lift.
  const lastPr = records.events[records.events.length - 1];
  const effectiveProgressExercise = progressExercise || lastPr?.exercise || liftRows[0]?.name || allLoggedExercises[0] || '';

  const selectExercise = useCallback((name) => {
    setProgressExercise(name);
    // The tapped row can be far from the chart; bring the chart into view.
    window.requestAnimationFrame(() => {
      const el = progressRef.current;
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const assignMuscle = useCallback((name, primary, secondary) => {
    if (!setState) return;
    // Keyed by name, so it merges per exercise with Claude's edits.
    setState(prev => {
      const meta = prev.exerciseMeta && typeof prev.exerciseMeta === 'object' ? prev.exerciseMeta : {};
      return { ...prev, exerciseMeta: { ...meta, [name]: { ...(meta[name] || {}), primary, secondary } } };
    });
  }, [setState]);

  // Fair consistency numbers: planned rest never breaks a streak, a pending
  // today doesn't either, and this week only counts days that have passed.
  const { sessions, cycleAnchor } = state;
  const trainingDays = profile?.trainingDays;
  const schedule = useMemo(() => {
    const s = { logs, plan, sessions, cycleAnchor, profile: { trainingDays } };
    return { streak: sessionStreak(s, today), weeks: weeklyStreak(s, today), week: complianceSoFar(s, today) };
  }, [logs, plan, sessions, cycleAnchor, trainingDays, today]);
  const { streak, weeks, week } = schedule;

  // Likely duplicate names for the badge on the Names button; memoised on
  // logs because suggestAliases compares every pair of names.
  const duplicates = useMemo(() => suggestAliases(logs).length, [logs]);

  const onMerged = useCallback((from, to) => {
    setProgressExercise(cur => (cur === from ? to : cur));
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="fit-quad" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <StatTile t={t} label="Streak" value={streak.current} accent={streak.current >= 3}
          hint={`sessions · best ${streak.best}`} />
        <StatTile t={t} label="Weekly streak" value={`${weeks.current}w`} accent={weeks.current >= 2}
          hint={`${weeks.target}+ a week · best ${weeks.best}w`} />
        <StatTile t={t} label="This Week" value={`${week.done}/${week.plannedSoFar}`}
          accent={week.plannedSoFar > 0 && week.done >= week.plannedSoFar}
          hint={week.remaining > 0 ? `so far · ${week.remaining} to go`
            : !week.plannedWeek ? 'nothing planned'
              : week.done >= week.plannedSoFar ? 'all done' : `${week.plannedSoFar - week.done} missed`} />
        <StatTile t={t} label="30-Day Volume" value={`${(stats.last30Volume / 1000).toFixed(1)}k`}
          hint={`${stats.last30Count} sessions · ${stats.total} all-time`} />
      </div>

      <GoalProgressCard t={t} p={weightGoal} calorieTarget={profile?.calorieTarget} />

      <WeekReviewCard t={t} isDark={isDark} state={state} today={today} events={records.events} />

      {weightSeries.length > 1 && (
        <Card t={t} padding={20}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>Bodyweight</div>
            <div style={{ fontSize: 11, color: t.textFaint }}>
              daily <span style={{ color: t.accent, fontWeight: 700 }}>&middot; 7-day average</span>
            </div>
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weightSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={t.divider} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: t.textFaint, fontSize: 11 }} stroke={t.divider} />
                {/* Weight moves in a narrow band, so a 0-based axis would
                    flatten the line into something unreadable. */}
                <YAxis domain={[min => Math.floor((goalWeight !== null ? Math.min(min, goalWeight) : min) - 1), 'dataMax + 1']}
                  tick={{ fill: t.textFaint, fontSize: 11 }} stroke={t.divider} width={42} />
                {goalWeight !== null && (
                  <ReferenceLine y={goalWeight} stroke={t.accent} strokeDasharray="4 4"
                    label={{ value: `goal ${goalWeight}`, position: 'insideBottomRight', fill: t.textFaint, fontSize: 10 }} />
                )}
                <Tooltip
                  contentStyle={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: t.text }}
                  itemStyle={{ color: t.text }} />
                <Line type="monotone" dataKey="weight" name="Daily" stroke={t.textFaint} strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="avg" name="7-day avg" stroke={t.accent} strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <BodyCard t={t} state={state} setState={setState} today={today} />

      <PrWallCard t={t} isDark={isDark} records={records} today={today} exerciseMeta={exerciseMeta}
        selected={effectiveProgressExercise} onSelect={selectExercise} />

      {/* scrollMarginTop keeps the card clear of the sticky header. */}
      <div ref={progressRef} style={{ scrollMarginTop: 110 }}>
        <ProgressCard t={t} isDark={isDark} logs={logs} exerciseMeta={exerciseMeta} programs={programs}
          events={records.events} exercises={allLoggedExercises}
          selected={effectiveProgressExercise} onSelect={setProgressExercise}
          onOpenNames={setState ? () => setNamesOpen(true) : null} duplicates={duplicates} />
      </div>

      <LiftStatusCard t={t} isDark={isDark} rows={liftRows} selected={effectiveProgressExercise} onSelect={selectExercise} />

      <MuscleVolumeCard t={t} isDark={isDark} volume={volume} plan={plan} exerciseMeta={exerciseMeta}
        onAssign={assignMuscle} />

      <Card t={t} padding={20}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 14 }}>Volume per week</div>
        <div style={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.weeks}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.divider} vertical={false} />
              <XAxis dataKey="label" tick={{ fill: t.textFaint, fontSize: 11 }} stroke={t.divider} />
              <YAxis tick={{ fill: t.textFaint, fontSize: 11 }} stroke={t.divider} />
              <Tooltip
                contentStyle={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: t.text }}
                itemStyle={{ color: t.text }}
                cursor={{ fill: t.surfaceAlt }}
              />
              <Bar dataKey="volume" fill={t.accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {namesOpen && (
        <ExerciseNamesSheet t={t} isDark={isDark} state={state} setState={setState} today={today}
          onMerged={onMerged} onClose={() => setNamesOpen(false)} />
      )}
    </div>
  );
}
